/* eslint-disable max-lines-per-function */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, InputAdornment, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword } from '../../store/userSlice';
import Loader from "../../utils/Loader";
import en from '../../nls/en.json';

const ChangePasswordModal = ({ showModal, handleClose, passWordUpdated }) => {
    const { loader } = useSelector(({ common }) => common);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });

    //To overcome the following issue- MUI popup gets closed on clicking TAB key 
    const stopPropagationForTab = (event) => {
        if (event.key === "Tab") {
            event.stopPropagation();
        }
    };

    const dispatch = useDispatch();
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const [errorMessages, setErrorMessages] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    //Putting setErrorMsg inside useEffect to show error msg everytime the field changes
    useEffect(()=>{
        setErrorMessages({
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        });
    },[formData.currentPassword, formData.newPassword, formData.confirmNewPassword]);

    const handleSubmit = () => {
        const { currentPassword, newPassword, confirmNewPassword } = formData;

        // // Check if any field is empty
        if (!currentPassword.trim() && !newPassword.trim() && !confirmNewPassword.trim()) {
            setErrorMessages({ currentPassword: en.CURRENT_PASSWORD_REQUIRED, newPassword: en.NEW_PASSWORD_REQUIRED, confirmNewPassword: en.CONFIRM_PASSWORD_REQUIRED });
            return;
        }
        if (!currentPassword.trim() && !newPassword.trim()) {
            setErrorMessages({ ...errorMessages, currentPassword: en.CURRENT_PASSWORD_REQUIRED, newPassword: en.NEW_PASSWORD_REQUIRED});
            return;
        }
        if (!newPassword.trim() && !confirmNewPassword.trim()) {
            setErrorMessages({ ...errorMessages, newPassword: en.NEW_PASSWORD_REQUIRED, confirmNewPassword: en.CONFIRM_PASSWORD_REQUIRED });
            return;
        }
        if (!currentPassword.trim() && !confirmNewPassword.trim()) {
            setErrorMessages({ ...errorMessages, currentPassword: en.CURRENT_PASSWORD_REQUIRED, confirmNewPassword: en.CONFIRM_PASSWORD_REQUIRED });
            return;
        }
        // // Check if currentPassword field is empty
        if (!currentPassword.trim()) {
            setErrorMessages({ ...errorMessages, currentPassword: en.CURRENT_PASSWORD_REQUIRED });
            return;
        }
        // Check if newPassword field is empty
        if (!newPassword.trim()) {
            setErrorMessages({ ...errorMessages, newPassword: en.NEW_PASSWORD_REQUIRED });
            return;
        }
        if(newPassword?.length<8){
            setErrorMessages({ ...errorMessages, newPassword: en.PASSWORD_CHARACTER_ERROR });
            return;
        }
        if(!/[A-Z]/g.test(newPassword)){ 
            setErrorMessages({ ...errorMessages, newPassword: en.PASSWORD_CAPITAL_ERROR });
            return;
        }
        if (!/\d/.test(newPassword) || !/[!@#$%&?]/.test(newPassword)) {
            setErrorMessages({ ...errorMessages, newPassword: en.PASSWORD_NO_SPECIALCHAR_ERROR });
            return;
        }

        // Check if new password is the same as current password
        if (newPassword === currentPassword) {
            setErrorMessages({ ...errorMessages, newPassword: en.NEW_CURRENT_PASS_MUST_DIFF });
            return;
        }
        if (!confirmNewPassword.trim()) {
            setErrorMessages({ ...errorMessages, confirmNewPassword: en.CONFIRM_PASSWORD_REQUIRED });
            return;
        }

        // Check if passwords match
        if (newPassword !== confirmNewPassword) {
            setErrorMessages({ ...errorMessages, confirmNewPassword: en.NEW_CONFIRM_PASS_NOMATCH });
            return;
        }
        const payload = {
            "old_password": formData?.currentPassword,
            "new_password": formData?.newPassword,
            "confirm_password": formData?.confirmNewPassword
        }

        //if sucessfully changed success pop else show error
        const callBack = (data) => {
            if (data?.response?.data?.messageKey === "ERROR") {
                setErrorMessages({ ...errorMessages, currentPassword: 'Incorrect Current Password' });
            } else {
                handleClose();
                    setFormData({
                        currentPassword: '',
                        newPassword: '',
                        confirmNewPassword: '',
                    });
                    passWordUpdated(data)
            }
            
        }
        dispatch(changePassword(payload, callBack ));
    };

    const handleToggleShowPassword = (field) => {
        switch (field) {
            case 'currentPassword':
                setShowCurrentPassword(!showCurrentPassword);
                break;
            case 'newPassword':
                setShowNewPassword(!showNewPassword);
                break;
            case 'confirmNewPassword':
                setShowConfirmNewPassword(!showConfirmNewPassword);
                break;
            default:
                break;
        }
    };

    return (

        //added onKeyDown={stopPropagationForTab} to fix MUI popup closing on clicking TAB key
        <Dialog open={showModal} onClose={handleClose} onKeyDown={stopPropagationForTab}

            //added 2 props below to reduce the size of Change Password Poup
            fullWidth
            maxWidth="xs"
        >
            {loader && <Loader loader={true} data-testid="loader-component"></Loader>}
            <DialogTitle>Change Password</DialogTitle>
            <DialogContent>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    variant="outlined"
                    id="currentPassword"
                    name="currentPassword"
                    label="Current Password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={formData.currentPassword}
                    onChange={handleChange}
                    sx={{ mb: 0 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => handleToggleShowPassword('currentPassword')}
                                    sx={{ color: '#1468A2' }}
                                    edge="end"
                                    aria-label="toggle password visibility"
                                    >
                                    {showCurrentPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                {errorMessages.currentPassword && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errorMessages.currentPassword}
                    </Typography>
                )}
                <TextField
                    id="newPassword"
                    name="newPassword"
                    label="New Password"
                    required
                    type={showNewPassword ? 'text' : 'password'}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={formData.newPassword}
                    onChange={handleChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => handleToggleShowPassword('newPassword')}
                                    edge="end"
                                    sx={{ color: '#1468A2' }}
                                >
                                    {showNewPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                {errorMessages.newPassword && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errorMessages.newPassword}
                    </Typography>
                )}
                <TextField
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    label="Confirm New Password"
                    required
                    type={showConfirmNewPassword ? 'text' : 'password'}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={formData.confirmNewPassword}
                    onChange={handleChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton 
                                    onClick={() => handleToggleShowPassword('confirmNewPassword')}
                                    sx={{ color: '#1468A2' }}
                                    edge="end"
                                    aria-label="toggle password visibility"
                                >
                                    {showConfirmNewPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                {errorMessages.confirmNewPassword && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errorMessages.confirmNewPassword}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button sx={{
                    fontFamily: "Poppins, sans-serif",
                    backgroundColor: "#1468A2",
                    color: "#fff",
                    "&:hover": {
                        backgroundColor: "#0e5490",
                    },
                }}
                    onClick={handleSubmit}
                    color="primary">Change Password</Button>
            </DialogActions>
        </Dialog>
    );
};

ChangePasswordModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    passWordUpdated: PropTypes.func
};

export default ChangePasswordModal;